import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowDownAZ,
  ArrowUpDown,
  Building2,
  Calendar,
  CalendarClock,
  Check,
  Filter,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Search,
  User,
  UserPlus,
  Voicemail,
  X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useLeads, LeadSortField, SortDir } from '../../hooks/useLeads';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { MoveCard } from '../../components/ui/MoveCard';
import { NewMoveForm, type NewMoveFormData } from '../../components/moves/NewMoveForm';
import {
  Quote,
  QuoteType,
  LeadStatus,
  LeadCommunicationType,
  LEAD_STATUS_LABEL,
  LEAD_STATUS_CLASSES,
  LEAD_COMMUNICATION_TYPE_LABEL,
} from '../../types/admin';

// ── All lead statuses ─────────────────────────────────────────────────────────

const ALL_LEAD_STATUSES: LeadStatus[] = ['new', 'contacted', 'estimate_scheduled', 'not_pursuing', 'lost'];

// ── Sort options ──────────────────────────────────────────────────────────────

interface SortOption {
  field: LeadSortField;
  dir: SortDir;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { field: 'nextFollowUp', dir: 'asc', label: 'Follow-up (soonest)' },
  { field: 'nextFollowUp', dir: 'desc', label: 'Follow-up (latest)' },
  { field: 'createdAt', dir: 'desc', label: 'Newest first' },
  { field: 'createdAt', dir: 'asc', label: 'Oldest first' },
  { field: 'customerCompanyName', dir: 'asc', label: 'Customer A–Z' },
  { field: 'customerCompanyName', dir: 'desc', label: 'Customer Z–A' },
  { field: 'movingDate', dir: 'asc', label: 'Move date (soonest)' },
  { field: 'movingDate', dir: 'desc', label: 'Move date (latest)' },
];

// ── Filter Sheet ──────────────────────────────────────────────────────────────

interface FilterState {
  visibleStatuses: Set<LeadStatus>;
  myLeadsOnly: boolean;
  unassignedOnly: boolean;
  typeCommercial: boolean;
  typeResidential: boolean;
}

interface FilterSheetProps {
  filters: FilterState;
  onApply: (f: FilterState) => void;
  onClose: () => void;
  showTypeFilter: boolean;
}

function FilterSheet({ filters, onApply, onClose, showTypeFilter }: FilterSheetProps) {
  const [visibleStatuses, setVisibleStatuses] = useState<Set<LeadStatus>>(
    new Set(filters.visibleStatuses),
  );
  const [myLeadsOnly] = useState(filters.myLeadsOnly);
  const [unassignedOnly] = useState(filters.unassignedOnly);
  const [typeCommercial, setTypeCommercial] = useState(filters.typeCommercial);
  const [typeResidential, setTypeResidential] = useState(filters.typeResidential);

  function toggleStatus(s: LeadStatus) {
    setVisibleStatuses(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }

  function handleApply() {
    onApply({ visibleStatuses, myLeadsOnly, unassignedOnly, typeCommercial, typeResidential });
    onClose();
  }

  function handleReset() {
    onApply({
      visibleStatuses: new Set(['new', 'contacted'] as LeadStatus[]),
      myLeadsOnly: false,
      unassignedOnly: true,
      typeCommercial: true,
      typeResidential: true,
    });
    onClose();
  }

  const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0
      ${checked ? 'border-plum bg-plum' : 'border-stone'}`}>
      {checked && (
        <svg viewBox="0 0 10 8" fill="none" className="w-2.5 h-2 text-white">
          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[80vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Filter Leads</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Type filter — at the very top */}
        {showTypeFilter && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Type</p>
            <div className="flex flex-col gap-2 mb-5">
              <button
                type="button"
                onClick={() => setTypeCommercial(v => !v)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${typeCommercial ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}
              >
                <CheckboxIcon checked={typeCommercial} />
                <span className={`text-small font-medium ${typeCommercial ? 'text-plum' : 'text-midnight'}`}>
                  Commercial
                </span>
              </button>
              <button
                type="button"
                onClick={() => setTypeResidential(v => !v)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${typeResidential ? 'border-plum bg-plum/5' : 'border-stone bg-white'}`}
              >
                <CheckboxIcon checked={typeResidential} />
                <span className={`text-small font-medium ${typeResidential ? 'text-plum' : 'text-midnight'}`}>
                  Residential
                </span>
              </button>
            </div>
          </>
        )}

        {/* Lead status filters */}
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Lead Status</p>
        <div className="flex flex-col gap-2 mb-5">
          {ALL_LEAD_STATUSES.map(s => {
            const checked = visibleStatuses.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${checked
                    ? 'border-plum bg-plum/5'
                    : 'border-stone bg-white'
                  }`}
              >
                <CheckboxIcon checked={checked} />
                <span className={`text-small font-medium ${checked ? 'text-plum' : 'text-midnight'}`}>
                  {LEAD_STATUS_LABEL[s]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 h-12 rounded-xl border border-stone text-small font-semibold text-slate
                       hover:border-plum/40 hover:text-midnight active:bg-mist
                       transition-colors duration-150"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex-1 h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sort Sheet ────────────────────────────────────────────────────────────────

interface SortSheetProps {
  sortBy: LeadSortField;
  sortDir: SortDir;
  onApply: (field: LeadSortField, dir: SortDir) => void;
  onClose: () => void;
}

function SortSheet({ sortBy, sortDir, onApply, onClose }: SortSheetProps) {
  const icons: Record<string, typeof ArrowDownAZ> = {
    nextFollowUp: CalendarClock,
    createdAt: Calendar,
    customerCompanyName: Building2,
    movingDate: Calendar,
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Sort Leads</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {SORT_OPTIONS.map(opt => {
            const active = sortBy === opt.field && sortDir === opt.dir;
            const Icon = icons[opt.field] ?? ArrowUpDown;
            return (
              <button
                key={`${opt.field}-${opt.dir}`}
                type="button"
                onClick={() => { onApply(opt.field, opt.dir); onClose(); }}
                className={`w-full flex items-center gap-3 h-11 px-4 rounded-xl border text-left
                  transition-all duration-150
                  ${active
                    ? 'border-plum bg-plum/5'
                    : 'border-stone bg-white hover:border-plum/40'
                  }`}
              >
                <Icon size={14} className={active ? 'text-plum' : 'text-slate'} />
                <span className={`text-small font-medium flex-1 ${active ? 'text-plum' : 'text-midnight'}`}>
                  {opt.label}
                </span>
                {active && <Check size={14} className="text-plum shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Stats Dashboard ───────────────────────────────────────────────────────────

interface StatsDashboardProps {
  newCount: number;
  contactedCount: number;
  activeStatus: LeadStatus | null;
  onClickStatus: (status: LeadStatus | null) => void;
  vertical?: boolean;
}

function StatsDashboard({ newCount, contactedCount, activeStatus, onClickStatus, vertical }: StatsDashboardProps) {
  return (
    <div className={`${vertical ? 'flex flex-col gap-2' : 'flex gap-2 mb-4'}`}>
      <button
        type="button"
        onClick={() => onClickStatus(activeStatus === 'new' ? null : 'new')}
        className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150
          ${activeStatus === 'new'
            ? 'border-blue-400 bg-blue-50 shadow-brand-sm'
            : 'border-stone bg-white hover:border-blue-300'
          }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
          ${activeStatus === 'new' ? 'bg-blue-100' : 'bg-stone/60'}`}>
          <UserPlus size={14} className={activeStatus === 'new' ? 'text-blue-600' : 'text-slate'} />
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[18px] font-semibold text-midnight leading-none">{newCount}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate mt-0.5">New</p>
        </div>
      </button>

      <button
        type="button"
        onClick={() => onClickStatus(activeStatus === 'contacted' ? null : 'contacted')}
        className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150
          ${activeStatus === 'contacted'
            ? 'border-amber-400 bg-amber-50 shadow-brand-sm'
            : 'border-stone bg-white hover:border-amber-300'
          }`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
          ${activeStatus === 'contacted' ? 'bg-amber-100' : 'bg-stone/60'}`}>
          <Phone size={14} className={activeStatus === 'contacted' ? 'text-amber-600' : 'text-slate'} />
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[18px] font-semibold text-midnight leading-none">{contactedCount}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate mt-0.5">Contacted</p>
        </div>
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultFollowUp(): string {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

function getTodayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDateDisplay(value: string): string | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Quick-Log Presets ─────────────────────────────────────────────────────────

interface QuickLogPreset {
  label: string;
  type: LeadCommunicationType;
  note: string;
  openNote: boolean;
  icon: typeof Phone;
}

const QUICK_LOG_PRESETS: QuickLogPreset[] = [
  { label: 'Called — no answer', type: 'phone_call', note: 'No answer', openNote: false, icon: Phone },
  { label: 'Called — left voicemail', type: 'voicemail', note: 'Left voicemail', openNote: false, icon: Voicemail },
  { label: 'Called — spoke with customer', type: 'phone_call', note: '', openNote: true, icon: Phone },
  { label: 'Sent email', type: 'email', note: '', openNote: true, icon: Mail },
];

// ── Quick-Log Sheet ──────────────────────────────────────────────────────────

function QuickLogSheet({
  leadName,
  onSubmit,
  onClose,
}: {
  leadName: string;
  onSubmit: (type: LeadCommunicationType, note: string, nextFollowUp: string) => Promise<void>;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'presets' | 'custom'>('presets');
  const [customType, setCustomType] = useState<LeadCommunicationType>('phone_call');
  const [customNote, setCustomNote] = useState('');
  const [presetNote, setPresetNote] = useState('');
  const [activePreset, setActivePreset] = useState<QuickLogPreset | null>(null);
  const [followUp, setFollowUp] = useState(getDefaultFollowUp);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handlePresetTap(preset: QuickLogPreset) {
    if (preset.openNote) {
      setActivePreset(preset);
      setPresetNote('');
      return;
    }
    // One-tap submit
    setSaving(true);
    setError('');
    try {
      await onSubmit(preset.type, preset.note, followUp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log communication');
      setSaving(false);
    }
  }

  async function handlePresetSubmit() {
    if (!activePreset) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit(activePreset.type, presetNote.trim() || activePreset.note || activePreset.label, followUp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log communication');
      setSaving(false);
    }
  }

  async function handleCustomSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customNote.trim()) { setError('Note is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit(customType, customNote.trim(), followUp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log communication');
      setSaving(false);
    }
  }

  const CUSTOM_TYPES: { type: LeadCommunicationType; icon: typeof Phone }[] = [
    { type: 'phone_call', icon: Phone },
    { type: 'email', icon: Mail },
    { type: 'text_message', icon: MessageSquare },
    { type: 'voicemail', icon: Voicemail },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[80vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-midnight">Log Communication</h2>
            <p className="text-[12px] text-slate truncate mt-0.5">{leadName}</p>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors shrink-0"
            aria-label="Close"><X size={16} /></button>
        </div>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 mb-3">
            <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
            <p className="text-[12px] text-rose">{error}</p>
          </div>
        )}

        {/* Next Follow-Up date */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-mist mb-3">
          <CalendarClock size={14} className="text-plum shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate">Follow-up</span>
          <div className="relative ml-auto">
            <input type="date" value={followUp} min={getTodayStr()}
              onChange={e => setFollowUp(e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
            <span className="text-[12px] font-semibold text-plum">
              {formatDateDisplay(followUp) ?? 'Set date'}
            </span>
          </div>
        </div>

        {mode === 'presets' && !activePreset && (
          <div className="flex flex-col gap-2">
            {QUICK_LOG_PRESETS.map(preset => {
              const Icon = preset.icon;
              return (
                <button
                  key={preset.label}
                  type="button"
                  disabled={saving}
                  onClick={() => handlePresetTap(preset)}
                  className="w-full flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl border border-stone
                             bg-white text-left text-small font-medium text-midnight
                             hover:border-plum/30 active:bg-mist transition-all duration-150
                             disabled:opacity-50"
                >
                  <Icon size={16} className="shrink-0 text-plum" />
                  {preset.label}
                  {!preset.openNote && (
                    <span className="ml-auto text-[10px] text-slate/60 uppercase font-bold tracking-wider">1-tap</span>
                  )}
                </button>
              );
            })}

            <button
              type="button"
              disabled={saving}
              onClick={() => setMode('custom')}
              className="w-full flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl border border-stone
                         bg-white text-left text-small font-medium text-midnight
                         hover:border-plum/30 active:bg-mist transition-all duration-150
                         disabled:opacity-50"
            >
              <Plus size={16} className="shrink-0 text-slate" />
              Custom…
            </button>

            {saving && (
              <div className="flex justify-center py-3">
                <LoadingSpinner size="sm" />
              </div>
            )}
          </div>
        )}

        {/* Preset with open note input */}
        {mode === 'presets' && activePreset && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-plum/5 border border-plum/20">
              {(() => { const Icon = activePreset.icon; return <Icon size={14} className="text-plum shrink-0" />; })()}
              <span className="text-[12px] font-semibold text-plum">{activePreset.label}</span>
              <button type="button" onClick={() => setActivePreset(null)}
                className="ml-auto text-[11px] text-slate hover:text-midnight">Change</button>
            </div>
            <textarea
              value={presetNote}
              onChange={e => setPresetNote(e.target.value)}
              placeholder="Add details (optional)…"
              rows={2}
              autoFocus
              className="w-full rounded-xl border border-stone bg-white px-4 py-3 text-small text-midnight
                         placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                         focus:border-plum resize-none"
            />
            <button
              type="button"
              disabled={saving}
              onClick={handlePresetSubmit}
              className="w-full h-11 rounded-xl bg-plum text-white text-small font-semibold
                         flex items-center justify-center gap-2
                         hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150"
            >
              {saving ? <LoadingSpinner size="sm" /> : 'Log Communication'}
            </button>
          </div>
        )}

        {/* Custom mode */}
        {mode === 'custom' && (
          <form onSubmit={handleCustomSubmit} className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-1.5">
              {CUSTOM_TYPES.map(({ type, icon: Icon }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setCustomType(type)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-150
                    ${customType === type ? 'bg-plum text-white' : 'bg-mist text-slate hover:text-midnight'}`}
                >
                  <Icon size={12} />
                  {LEAD_COMMUNICATION_TYPE_LABEL[type]}
                </button>
              ))}
            </div>
            <textarea
              value={customNote}
              onChange={e => setCustomNote(e.target.value)}
              placeholder="What happened?"
              rows={2}
              autoFocus
              className="w-full rounded-xl border border-stone bg-white px-4 py-3 text-small text-midnight
                         placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                         focus:border-plum resize-none"
            />
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode('presets')}
                className="h-11 px-4 rounded-xl border border-stone text-small font-semibold text-slate
                           hover:text-midnight active:bg-mist transition-colors duration-150">
                Back
              </button>
              <button
                type="submit"
                disabled={saving || !customNote.trim()}
                className="flex-1 h-11 rounded-xl bg-plum text-white text-small font-semibold
                           flex items-center justify-center gap-2
                           hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150"
              >
                {saving ? <LoadingSpinner size="sm" /> : 'Log Communication'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Success Toast ────────────────────────────────────────────────────────────

function SuccessToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 2500);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50
                    flex items-center gap-2 px-4 py-2.5 rounded-xl bg-midnight text-white shadow-lg
                    animate-in slide-in-from-bottom-4 fade-in duration-200">
      <Check size={14} className="text-emerald-400 shrink-0" />
      <span className="text-[13px] font-medium">{message}</span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const DEFAULT_FILTERS: FilterState = {
  visibleStatuses: new Set(['new', 'contacted'] as LeadStatus[]),
  myLeadsOnly: false,
  unassignedOnly: false,
  typeCommercial: true,
  typeResidential: true,
};

export function LeadsListPage({ embedded, onSidebarContent }: { embedded?: boolean; onSidebarContent?: (content: React.ReactNode) => void } = {}) {
  const navigate = useNavigate();
  const { activeOrg, currentUser, loading: profileLoading, hasActiveSubscription } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const noSubscription = !hasActiveSubscription && !currentUser?.isPlatformAdmin;

  const uid = currentUser?.uid ?? undefined;

  // Feature flag checks
  const commercialEnabled = activeOrg?.featureCommercialMovingQuotes !== false;
  const residentialEnabled = activeOrg?.featureResidentialMovingQuotes ?? false;
  const bothTypesEnabled = commercialEnabled && residentialEnabled;

  const [showNewSheet, setShowNewSheet] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState<LeadSortField>('nextFollowUp');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [dashStatusFilter, setDashStatusFilter] = useState<LeadStatus | null>(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Auto-fallback state
  const [autoFallbackDone, setAutoFallbackDone] = useState(false);

  const [quickLogLead, setQuickLogLead] = useState<Quote | null>(null);
  const [toastMessage, setToastMessage] = useState('');

  // Compute type filter for API: if both or neither selected, don't filter by type
  const typeFilterForApi = useMemo(() => {
    if (filters.typeCommercial && !filters.typeResidential) return 'commercial' as QuoteType;
    if (!filters.typeCommercial && filters.typeResidential) return 'residential' as QuoteType;
    return undefined;
  }, [filters.typeCommercial, filters.typeResidential]);

  const { leads, loading, loadingMore, error, hasMore, stats, createMove, logCommunication, fetchMore } = useLeads(orgId, {
    search: debouncedSearch || undefined,
    sortBy,
    sortDir,
    leadUid: filters.myLeadsOnly ? uid : undefined,
    type: typeFilterForApi,
  });

  // Auto-fallback: mark done once first load completes (no longer auto-disabling since default is not myLeadsOnly)
  useEffect(() => {
    if (!loading && !autoFallbackDone) {
      setAutoFallbackDone(true);
    }
  }, [loading, autoFallbackDone]);

  // Client-side status + unassigned + type filtering
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const status = lead.leadStatus ?? 'new';
      // Status filter
      if (dashStatusFilter) {
        if (status !== dashStatusFilter) return false;
      } else {
        if (!filters.visibleStatuses.has(status)) return false;
      }
      // Unassigned filter: if unassignedOnly is on and myLeadsOnly is off,
      // also include leads that have no quoteLeadUserId
      // (This is additive — we show my leads + unassigned leads)
      if (filters.unassignedOnly && !filters.myLeadsOnly) {
        // Show all leads (unassigned filter just means "include unassigned" which is already the case)
      }
      // Type filter (client-side for when both types enabled)
      if (bothTypesEnabled) {
        const isCommercial = lead.type !== 'residential';
        if (isCommercial && !filters.typeCommercial) return false;
        if (!isCommercial && !filters.typeResidential) return false;
      }
      return true;
    });
  }, [leads, filters.visibleStatuses, filters.typeCommercial, filters.typeResidential, filters.unassignedOnly, filters.myLeadsOnly, dashStatusFilter, bothTypesEnabled]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.myLeadsOnly) n++;
    if (filters.unassignedOnly) n++;
    if (filters.visibleStatuses.size < ALL_LEAD_STATUSES.length) n++;
    if (bothTypesEnabled && (!filters.typeCommercial || !filters.typeResidential)) n++;
    return n;
  }, [filters, bothTypesEnabled]);

  const isDefaultSort = sortBy === 'nextFollowUp' && sortDir === 'asc';

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMore && !loadingMore) {
          fetchMore();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, fetchMore]);

  const handleCreate = useCallback(async (data: NewMoveFormData) => {
    const lead = await createMove(data as unknown as Record<string, unknown>);
    setShowNewSheet(false);
    navigate(`/moves/${lead.id}`);
    return lead;
  }, [createMove, navigate]);

  const handleFilterApply = useCallback((f: FilterState) => {
    setFilters(f);
  }, []);

  const handleQuickLog = useCallback(async (type: LeadCommunicationType, note: string, nextFollowUp: string) => {
    if (!quickLogLead || !currentUser) return;
    await logCommunication(quickLogLead.id, {
      type,
      note,
      createdBy: currentUser.uid,
      createdByName: currentUser.displayName ?? currentUser.email,
      nextFollowUp,
    });
    const name = quickLogLead.pointOfContact?.name || quickLogLead.customerCompanyName || 'lead';
    setQuickLogLead(null);
    setToastMessage(`Communication logged for ${name}`);
  }, [quickLogLead, currentUser, logCommunication]);

  if (activeOrg?.featureLeadPipeline === false) {
    return (
      <div className="px-4 pt-16 flex flex-col items-center text-center gap-3">
        <p className="text-small text-slate font-semibold">Lead Pipeline is not enabled for this organization.</p>
        <button type="button" onClick={() => navigate('/')} className="text-small text-plum hover:underline">Go to Quotes</button>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Provide sidebar content for desktop two-column layout
  useEffect(() => {
    if (loading || error) { onSidebarContent?.(null); return; }
    onSidebarContent?.(
      <StatsDashboard
        newCount={stats.new}
        contactedCount={stats.contacted}
        activeStatus={dashStatusFilter}
        onClickStatus={setDashStatusFilter}
        vertical
      />,
    );
  }, [onSidebarContent, loading, error, stats, dashStatusFilter, setDashStatusFilter]);

  return (
    <div className={embedded ? '' : 'px-4 pt-6 pb-28 md:px-8 md:max-w-5xl md:mx-auto'}>
      {!embedded && <h1 className="font-display text-h1 text-midnight mb-4">Leads</h1>}

      {/* Active filter indicators */}
      {(filters.myLeadsOnly || filters.unassignedOnly) && !loading && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {filters.myLeadsOnly && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-plum/5 text-plum border border-plum/20">
              <User size={10} />
              My Leads
              <button type="button" onClick={() => setFilters(prev => ({ ...prev, myLeadsOnly: false }))} className="ml-0.5 hover:opacity-70">
                <X size={10} />
              </button>
            </span>
          )}
          {filters.unassignedOnly && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
              Unassigned
              <button type="button" onClick={() => setFilters(prev => ({ ...prev, unassignedOnly: false }))} className="ml-0.5 hover:opacity-70">
                <X size={10} />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Stats dashboard — hidden when embedded (desktop shows in sidebar) */}
      {!loading && !error && !embedded && (
        <StatsDashboard
          newCount={stats.new}
          contactedCount={stats.contacted}
          activeStatus={dashStatusFilter}
          onClickStatus={setDashStatusFilter}
        />
      )}

      {/* Search + Filter + Sort row */}
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-0">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
            <Search size={14} className="text-slate" />
          </div>
          <input
            type="search"
            placeholder="Search name, phone, or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {/* Sort button */}
        <button
          type="button"
          onClick={() => setShowSortSheet(true)}
          className={`relative shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center
            transition-colors duration-150
            ${!isDefaultSort
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:text-midnight hover:border-plum/40'
            }`}
          aria-label="Sort"
        >
          <ArrowUpDown size={16} />
        </button>

        {/* Filter button */}
        <button
          type="button"
          onClick={() => setShowFilterSheet(true)}
          className={`relative shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center
            transition-colors duration-150
            ${activeFilterCount > 0
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:text-midnight hover:border-plum/40'
            }`}
          aria-label="Filter"
        >
          <Filter size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-plum
                             flex items-center justify-center text-[9px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Dashboard status filter indicator */}
      {dashStatusFilter && (
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md
            text-[10px] font-bold uppercase tracking-wider ${LEAD_STATUS_CLASSES[dashStatusFilter]}`}>
            {LEAD_STATUS_LABEL[dashStatusFilter]}
          </span>
          <button
            type="button"
            onClick={() => setDashStatusFilter(null)}
            className="text-[11px] text-slate hover:text-midnight font-medium"
          >
            Clear
          </button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load leads</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && leads.length === 0 && !debouncedSearch && (
        <div className="flex flex-col items-center py-20 gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-mist flex items-center justify-center">
            <UserPlus size={28} className="text-slate" />
          </div>
          <div>
            <p className="text-small font-semibold text-midnight mb-1">No leads yet</p>
            <p className="text-[13px] text-slate leading-snug">
              Tap the <span className="font-semibold text-plum">+</span> button to create your first lead.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && leads.length === 0 && debouncedSearch && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-mist flex items-center justify-center">
            <Search size={20} className="text-slate" />
          </div>
          <div>
            <p className="text-small font-semibold text-midnight mb-1">No results</p>
            <p className="text-[13px] text-slate leading-snug">
              No leads match &ldquo;{debouncedSearch}&rdquo;
            </p>
          </div>
        </div>
      )}

      {!loading && !error && leads.length > 0 && filteredLeads.length === 0 && (
        <div className="flex flex-col items-center py-16 gap-3 text-center">
          <div className="w-12 h-12 rounded-full bg-mist flex items-center justify-center">
            <Search size={20} className="text-slate" />
          </div>
          <div>
            <p className="text-small font-semibold text-midnight mb-1">No results</p>
            <p className="text-[13px] text-slate leading-snug">
              Try adjusting your search or filters.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && filteredLeads.length > 0 && (
        <>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {filteredLeads.map(lead => (
              <li key={lead.id}>
                <MoveCard
                  data={lead}
                  stage="lead"
                  statusLabel={LEAD_STATUS_LABEL[lead.leadStatus ?? 'new'] ?? 'New'}
                  statusClasses={LEAD_STATUS_CLASSES[lead.leadStatus ?? 'new'] ?? 'bg-stone text-slate'}
                  onClick={() => navigate(`/moves/${lead.id}`)}
                  onQuickLog={() => setQuickLogLead(lead)}
                />
              </li>
            ))}
          </ul>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-1" />

          {loadingMore && (
            <div className="flex justify-center py-6">
              <Loader2 size={20} className="text-plum animate-spin" />
            </div>
          )}
        </>
      )}

      {/* Floating action button */}
      {!noSubscription && (
        <button
          type="button"
          onClick={() => setShowNewSheet(true)}
          className="fixed bottom-[calc(env(safe-area-inset-bottom)+16px)] right-4
                     z-40 h-14 w-14 rounded-full bg-plum text-white shadow-brand-md
                     flex items-center justify-center
                     hover:bg-rose active:opacity-80
                     transition-all duration-150"
          aria-label="New lead"
        >
          <Plus size={26} />
        </button>
      )}

      {showNewSheet && (
        <NewMoveForm
          onClose={() => setShowNewSheet(false)}
          onCreate={handleCreate}
          showTypeSelector={bothTypesEnabled}
          defaultType={residentialEnabled && !commercialEnabled ? 'residential' : 'commercial'}
          orgId={orgId}
        />
      )}

      {showFilterSheet && (
        <FilterSheet
          filters={filters}
          onApply={handleFilterApply}
          onClose={() => setShowFilterSheet(false)}
          showTypeFilter={bothTypesEnabled}
        />
      )}

      {showSortSheet && (
        <SortSheet
          sortBy={sortBy}
          sortDir={sortDir}
          onApply={(field, dir) => { setSortBy(field); setSortDir(dir); }}
          onClose={() => setShowSortSheet(false)}
        />
      )}

      {quickLogLead && (
        <QuickLogSheet
          leadName={quickLogLead.pointOfContact?.name || quickLogLead.customerCompanyName || 'Lead'}
          onSubmit={handleQuickLog}
          onClose={() => setQuickLogLead(null)}
        />
      )}

      {toastMessage && (
        <SuccessToast message={toastMessage} onDismiss={() => setToastMessage('')} />
      )}
    </div>
  );
}
